/**
 * Utility functions for owner information display
 */

import type { OccupancyBadgeConfig } from '../types/intelligence';

/**
 * Extract initials from a person's name
 * @param name Full name of the person
 * @returns Initials (max 2 characters)
 */
export function getInitialsFromName(name: string): string {
  if (!name || typeof name !== 'string') {
    return '?';
  }

  const trimmed = name.trim();
  if (!trimmed) {
    return '?';
  }

  const parts = trimmed.split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return '?';
  }

  if (parts.length === 1) {
    // Single name: take first 2 characters
    return parts[0].substring(0, 2).toUpperCase();
  }

  // Multiple names: take first letter of first and last
  const firstInitial = parts[0][0];
  const lastInitial = parts[parts.length - 1][0];
  return (firstInitial + lastInitial).toUpperCase();
}

/**
 * Get badge configuration for occupancy status
 * @param ownerOccupied Whether the property is owner-occupied
 * @param isDark Whether dark mode is active
 * @returns Badge configuration with label and colors
 */
export function getOccupancyBadgeConfig(
  ownerOccupied: boolean | null,
  isDark: boolean
): OccupancyBadgeConfig | null {
  if (ownerOccupied === null) {
    return null;
  }

  if (ownerOccupied) {
    return {
      label: 'Owner Occupied',
      color: isDark ? '#10b981' : '#059669',
      backgroundColor: isDark ? 'rgba(16, 185, 129, 0.1)' : 'rgba(5, 150, 105, 0.1)',
    };
  } else {
    return {
      label: 'Investment Property',
      color: isDark ? '#3b82f6' : '#2563eb',
      backgroundColor: isDark ? 'rgba(59, 130, 246, 0.1)' : 'rgba(37, 99, 235, 0.1)',
    };
  }
}
